import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Boxes, ClipboardCheck, CalendarCheck, Ban, FileBarChart, DollarSign, Menu, Settings, LogOut, MapPin, ChevronDown, Check, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth, usePerms } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import SectionSwitcher from "@/components/SectionSwitcher";
import { RiggingSiteProvider, useRiggingSite } from "@/context/RiggingSiteContext";
import { api } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { to: "/rigging", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/rigging/inventory", label: "Rigging Inventory", icon: Boxes },
  { to: "/rigging/inspections", label: "Inspections", icon: ClipboardCheck },
  { to: "/rigging/quarterly", label: "Quarterly Round", icon: CalendarCheck },
  { to: "/rigging/quarantine", label: "Damage / Quarantine Bin", icon: Ban },
  { to: "/rigging/reports", label: "Reports", icon: FileBarChart },
  { to: "/rigging/cost-table", label: "Cost Table", icon: DollarSign },
];

function SidebarContent({ onNavigate }) {
  return (
    <div className="flex h-full flex-col">
      <SectionSwitcher section="rigging" onNavigate={onNavigate} />
      <nav className="flex-1 space-y-1 px-3">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`rig-nav-${item.to}`}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                  isActive ? "bg-brand/15 text-brand" : "text-ink2 hover:bg-surf-3 hover:text-ink"
                }`
              }
            >
              <Icon size={19} />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="px-5 py-4 text-[10px] uppercase tracking-widest text-ink3">BoreHub × Rigging</div>
    </div>
  );
}

function SiteSwitcher() {
  const { sites, siteId, setSiteId, siteName } = useRiggingSite();
  const { canManage } = usePerms();
  const [manage, setManage] = useState(false);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="rig-site-switcher" className="flex items-center gap-2 rounded-lg border border-hairline bg-surf-2 px-3 py-2 text-sm font-semibold text-ink hover:border-brand">
            <MapPin size={16} className="text-brand" />
            <span className="max-w-[160px] truncate">{siteName}</span>
            <ChevronDown size={15} className="text-ink3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[230px]">
          <DropdownMenuItem data-testid="rig-site-all" onClick={() => setSiteId("all")} className="flex items-center gap-2">
            <span className="flex-1">All sites</span>{siteId === "all" && <Check size={15} className="text-brand" />}
          </DropdownMenuItem>
          {sites.map((s) => (
            <DropdownMenuItem key={s.id} data-testid={`rig-site-${s.id}`} onClick={() => setSiteId(s.id)} className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate">{s.name}</span>{siteId === s.id && <Check size={15} className="text-brand" />}
            </DropdownMenuItem>
          ))}
          {canManage && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="rig-site-manage" onClick={() => setManage(true)}><Settings size={15} className="mr-2" /> Manage sites…</DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <ManageSitesDialog open={manage} onClose={() => setManage(false)} />
    </>
  );
}

function ManageSitesDialog({ open, onClose }) {
  const { sites, reload } = useRiggingSite();
  const [name, setName] = useState("");
  const [loc, setLoc] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await api.post("/rigging/sites", { name, location: loc }); setName(""); setLoc(""); reload(); toast.success("Site added"); }
    catch (e) { toast.error(e.message || "Failed"); }
    finally { setBusy(false); }
  };
  const del = async (s) => { try { await api.del(`/rigging/sites/${s.id}`); reload(); toast.success("Site removed"); } catch (e) { toast.error(e.message || "Failed"); } };
  const field = "w-full rounded-lg border border-hairline bg-surf px-3 py-2 text-sm text-ink outline-none focus:border-brand";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-hairline bg-surf-2">
        <DialogHeader><DialogTitle className="text-ink">Job sites</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {sites.length === 0 ? <p className="text-sm text-ink3">No sites yet. Add your first below.</p> : sites.map((s) => (
            <div key={s.id} className="flex items-center gap-2 rounded-lg border border-hairline bg-surf px-3 py-2">
              <MapPin size={15} className="text-brand" />
              <div className="min-w-0 flex-1"><div className="truncate font-semibold text-ink">{s.name}</div>{s.location ? <div className="truncate text-xs text-ink3">{s.location}</div> : null}</div>
              <button data-testid={`rig-site-del-${s.id}`} onClick={() => del(s)} className="rounded-lg p-1.5 text-ink3 hover:text-danger"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
        <div className="mt-3 border-t border-hairline pt-3">
          <input data-testid="rig-site-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Site name" className={`${field} mb-2`} />
          <input value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="Location (optional)" className={`${field} mb-2`} />
          <button data-testid="rig-site-add" onClick={add} disabled={busy} className="flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-extrabold text-black disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Add site</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function RiggingLayout({ children }) {
  return (
    <RiggingSiteProvider>
      <RiggingShell>{children}</RiggingShell>
    </RiggingSiteProvider>
  );
}

function RiggingShell({ children }) {
  const { user, logout } = useAuth();
  const { isAdmin } = usePerms();
  const { t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="flex min-h-screen bg-surf text-ink">
      <aside className="hidden w-64 shrink-0 border-r border-hairline bg-surf-2 lg:block">
        <div className="sticky top-0 h-screen"><SidebarContent /></div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-hairline bg-surf/95 px-4 py-3 backdrop-blur lg:px-6">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="rig-sidebar-toggle" className="rounded-lg p-2 text-ink2 hover:bg-surf-3 lg:hidden"><Menu size={22} /></button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 border-hairline bg-surf-2 p-0">
              <SidebarContent onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>

          <SiteSwitcher />

          <div className="ml-auto flex items-center gap-2">
            {isAdmin && (
              <button data-testid="rig-settings-button" onClick={() => navigate("/settings")} className="rounded-lg p-2 text-ink2 hover:bg-surf-3" title={t("settings")}><Settings size={20} /></button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="rig-user-menu" className="flex items-center gap-2 rounded-full border border-hairline bg-surf-2 py-1 pl-1 pr-3">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand text-xs font-extrabold text-black">{(user?.name || "U").charAt(0).toUpperCase()}</span>
                  <span className="hidden text-sm font-semibold sm:block">{user?.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[200px]">
                <div className="px-2 py-1.5">
                  <div className="text-sm font-semibold text-ink">{user?.name}</div>
                  <div className="text-xs text-ink3">{user?.role}</div>
                </div>
                <DropdownMenuItem data-testid="rig-logout-button" onClick={doLogout} className="text-danger">
                  <LogOut size={16} className="mr-2" /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="min-w-0 flex-1">{children}</main>
      </div>
    </div>
  );
}
